import { discoveryAttribution, measureDiscover, readExposureId } from "./discoverMeasurement";
import { bumpDiscoverAffinity } from "./discoverFeedback";
import { useCallback, useEffect, useRef, useState } from "react";
import { discoverClient } from "../discoverClient";
import type { NavigateOptions } from "@/app/routing/useAppNavigation";
import type { DiscoveryCardModel } from "./types";

type Intent = NonNullable<NavigateOptions["discoveryIntent"]>;
type Secret = { key: string; sessionId?: string; counted?: boolean };
type Surface = { kind: "browser" | "static" | "hosted" | "shared"; instance_id?: string };
const storageKey = (id: string) => `ato.discover-use.${id}`;
function restoredIntent(): Intent | null {
  const value = history.state?.atoDiscoveryIntent;
  return value && typeof value.requestId === "string" && typeof value.appId === "string" &&
    typeof value.publishedVersion === "string" ? value : null;
}

/** No use intent is inferred from a URL, a Library instance, or a video preview. */
export function useDiscoverUsage({ entry, visible, appUrl, surface, onCount, selectionIsDiscover = true }: {
  entry: DiscoveryCardModel | null; visible: boolean; appUrl: string | null;
  surface: Surface | null; onCount: (id: string, count: number) => void;
  selectionIsDiscover?: boolean;
}) {
  const [intent, setIntent] = useState<Intent | null>(restoredIntent);
  const intentRef = useRef(intent);
  intentRef.current = intent;
  const secrets = useRef(new Map<string, Secret>());
  const issuing = useRef(new Map<string, Promise<void>>());
  const confirming = useRef(new Map<string, Promise<number>>());
  const affinityCounted = useRef(new Set<string>());
  const categoryRef = useRef<string | null>(entry?.category ?? null);
  categoryRef.current = entry?.category ?? null;
  const [ready, setReady] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const [issuedRevision, setIssuedRevision] = useState(0);
  const appKey = entry?.selection.kind === "discover" && entry.publishedVersion
    ? `${entry.selection.id}:${entry.publishedVersion}` : null;
  const readSecret = useCallback((id: string) => {
    if (secrets.current.has(id)) return secrets.current.get(id)!;
    try {
      const value = JSON.parse(sessionStorage.getItem(storageKey(id)) ?? "null") as Secret | null;
      if (value && typeof value.key === "string") { secrets.current.set(id, value); return value; }
    } catch { /* Memory-only fallback when same-tab storage is unavailable. */ }
    return null;
  }, []);
  const saveSecret = useCallback((id: string, secret: Secret) => {
    secrets.current.set(id, secret);
    try { sessionStorage.setItem(storageKey(id), JSON.stringify(secret)); } catch { /* Same live session remains usable. */ }
  }, []);
  useEffect(() => {
    const restored = restoredIntent();
    if (!selectionIsDiscover) {
      intentRef.current = null;
      setIntent(null);
    } else if (restored && appKey === `${restored.appId}:${restored.publishedVersion}`) {
      intentRef.current = restored;
      setIntent(restored);
    } else if (appKey && intentRef.current && appKey !== `${intentRef.current.appId}:${intentRef.current.publishedVersion}`) {
      intentRef.current = null;
      setIntent(null);
    }
    setReady(null);
    setError(null);
  }, [appKey, selectionIsDiscover]);

  const begin = useCallback((card: DiscoveryCardModel): Intent | undefined => {
    if (card.selection.kind !== "discover" || !card.publishedVersion) return;
    const current = intentRef.current;
    if (current?.appId === card.selection.id && current.publishedVersion === card.publishedVersion && readSecret(current.requestId))
      return current;
    const next = { requestId: crypto.randomUUID(), appId: card.selection.id, publishedVersion: card.publishedVersion };
    saveSecret(next.requestId, { key: crypto.randomUUID() });
    intentRef.current = next;
    setIntent(next);
    setError(null);
    return next;
  }, [readSecret, saveSecret]);

  const onSurfaceReady = useCallback((url: string, bridge = true) => {
    if (!appKey || !appUrl || !surface || (!bridge && surface.kind !== "static")) return;
    try { if (new URL(url).origin === new URL(appUrl).origin) setReady(appKey); }
    catch { /* Non-web and opaque surfaces cannot confirm a public use. */ }
  }, [appKey, appUrl, surface?.kind, surface?.instance_id]);
  // Issue at deliberate selection, before readiness; a failed/prefetched frame
  // can leave a pending session but cannot increase the public count.
  useEffect(() => {
    if (!visible || !appKey || !intent || appKey !== `${intent.appId}:${intent.publishedVersion}`) return;
    const secret = readSecret(intent.requestId);
    if (!secret) { setError("This Discover session could not be restored. Select the app again."); return; }
    if (secret.sessionId) return;
    let cancelled = false;
    void Promise.resolve().then(async () => {
      if (cancelled) return;
      let pending = issuing.current.get(intent.requestId);
      if (!pending) {
        // A detail view is interest, even when measurement is switched off:
        // the local profile only follows the personalization flag.
        if (!affinityCounted.current.has(intent.requestId)) {
          affinityCounted.current.add(intent.requestId);
          bumpDiscoverAffinity(categoryRef.current);
        }
        // Read the impression this visit kept before sending anything: the
        // detail row and the Open session both join back to that exposure.
        const exposureId = readExposureId(intent.appId, intent.publishedVersion);
        await measureDiscover(intent.appId,intent.publishedVersion,"detail",
          exposureId ? { exposureId } : undefined);
        if(cancelled)return;
        pending = discoverClient.beginUsage(intent.appId, {
          request_id: intent.requestId, confirmation_key: secret.key, published_version: intent.publishedVersion, attribution: discoveryAttribution(),
          ...(exposureId ? { exposure_id: exposureId } : {}),
        }).then(issued => {
          secret.sessionId = issued.discovery_session_id;
          saveSecret(intent.requestId, secret);
        }).finally(() => issuing.current.delete(intent.requestId));
        issuing.current.set(intent.requestId, pending);
      }
      await pending;
      if (!cancelled) setIssuedRevision(value => value + 1);
    }).catch(cause => {
      if (!cancelled) setError(cause instanceof Error ? cause.message : "Discover session could not be issued.");
    });
    return () => { cancelled = true; };
  }, [appKey, intent, visible, revision, readSecret, saveSecret]);
  useEffect(() => {
    if (!visible || !appKey || ready !== appKey || !intent || !surface ||
      appKey !== `${intent.appId}:${intent.publishedVersion}`) return;
    const secret = readSecret(intent.requestId);
    if (!secret) { setError("This Discover session could not be restored. Select the app again."); return; }
    if (secret.counted || !secret.sessionId) return;
    let cancelled = false;
    void Promise.resolve().then(async () => {
      if (cancelled) return;
      let pending = confirming.current.get(intent.requestId);
      if (!pending) {
        pending = discoverClient.confirmUsage(intent.appId, secret.sessionId!, secret.key, surface).then(result => {
          secret.counted = true;
          saveSecret(intent.requestId, secret);
          return result.discover_run_count;
        }).finally(() => confirming.current.delete(intent.requestId));
        confirming.current.set(intent.requestId, pending);
      }
      const count = await pending;
      if (!cancelled) {
        onCount(intent.appId, count);
        setError(null);
      }
    }).catch(cause => {
      if (!cancelled) setError(cause instanceof Error ? cause.message : "Discover use could not be recorded.");
    });
    return () => { cancelled = true; };
  }, [appKey, intent, visible, ready, surface?.kind, surface?.instance_id, revision, issuedRevision, readSecret, saveSecret, onCount]);
  return { begin, intent, onSurfaceReady, error, retry: () => setRevision(value => value + 1) };
}
